import { createContext } from '@hover/blueprint';
import type { PropsWithChildren } from 'react';
import React, { useMemo, useState } from 'react';

import { useQueryParam } from 'src/shared/hooks/useQueryParam';

export interface EmbedContext {
  isEmbed: boolean;
  hasSubmittedLead: boolean;
  setHasSubmittedLead: (value: boolean) => void;
}

const defaultValue: EmbedContext = {
  isEmbed: false,
  hasSubmittedLead: false,
  setHasSubmittedLead: () => {},
};

const [Provider, useContext] = createContext<EmbedContext | undefined>({
  strict: false,
});

export interface EmbedProviderProps extends PropsWithChildren<{}> {}

export const EmbedProvider = (props: EmbedProviderProps) => {
  const hasEmbedQueryParam = useQueryParam('isEmbed') === 'true';
  const [isEmbed, setIsEmbed] = useState(hasEmbedQueryParam);
  const [hasSubmittedLead, setHasSubmittedLead] = useState(false);

  if (hasEmbedQueryParam && !isEmbed) setIsEmbed(true);

  const value = useMemo(
    () => ({ isEmbed, hasSubmittedLead, setHasSubmittedLead }),
    [isEmbed, hasSubmittedLead, setHasSubmittedLead],
  );

  return <Provider value={value} {...props} />;
};

export const useEmbedContext = () => useContext() ?? defaultValue;
